import $ from "cash-dom"
import Player from "@vimeo/player"
import signals from "signals"
import rollbarHelper from "./rollbar_helper"
import videoCaptionsManager from "./video_captions_manager"
import userSettings from "./user_settings"
import intervals from "intervals"
import soundHelper from "./sound_helper"
import modal from "./modal"

const vimeoManager = {
  players: {},

  init: () => {
    vimeoManager.players = {}
  },

  initPlayer: (iframe, options) => {
    const $iframe = $(iframe)
    const player = _createPlayer($iframe, options)
    vimeoManager.players[$iframe.attr("id")] = player
    return _trackAccumulativePlaytime(player)
  },

  // time: format should be hh:mm:ss/mm:ss/ss
  playVideo: (iframe, time) => {
    const player = _getPlayer(iframe)
    const vimeoPlayer = player.player
    const seconds = timeToSeconds(time)

    function onError(error) {
      rollbarHelper.sendErrorMsg(error.toString(), {
        time,
        seconds,
        method: "playVideo",
        playerState: player.state,
        iframeId: _getIframeId(iframe)
      })
    }

    // we can't use setCurrentTime(0) when the player hasn't started yet
    if (!player.state && !seconds) return vimeoPlayer.play().catch(onError)

    return vimeoPlayer.setCurrentTime(seconds)
      .then(() => {
        if (!soundHelper.canAutoPlay()) return

        vimeoPlayer.play().catch(onError)
      })
      .catch(error => onError(error, time, seconds, iframe))
  },

  pauseVideo: (iframe) => _getPlayer(iframe)?.player.pause(),

  pauseAll() {
    for (const player of Object.values(this.players)) {
      player.player.pause()
    }
  },

  loadNewVideo: (iframe, vimeoId) =>
    _getPlayer(iframe)
      ?.player.loadVideo(vimeoId)
      .catch((error) => {
        // the error name is "NotFoundError" if the video doesn't exist
        // and "TypeError" if it isn't an integer
        if (error.name === "NotFoundError" || error.name === "TypeError") {
          rollbarHelper.sendErrorMsg(error.toString(), {
            vimeoId,
            method: "loadNewVideo",
            iframeId: _getIframeId(iframe)
          })
        }
      }),

  enableCaptions: (iframe, language) =>
    _getPlayer(iframe)?.player.enableTextTrack(language),

  getVideoLanguage: (iframe) => _getPlayer(iframe)?.language,

  getAccumulativePlaytime: (iframe) => _getPlayer(iframe)?.playtime || 0,

  getDuration: (iframe) => _getPlayer(iframe)?.duration || 0,

  getCurrentTime: (iframe) => _getPlayer(iframe)?.currentTime || 0,

  skip(iframe, skipSeconds) {
    const player = _getPlayer(iframe)
    if (!player) return

    // https://developer.vimeo.com/player/sdk/
    // reference#set-the-playback-position-of-a-video
    const seconds = this.getCurrentTime(iframe) + skipSeconds
    player.player?.setCurrentTime(seconds).catch(error => {
      if (error.name === "RangeError") return

      rollbarHelper.sendErrorMsg(error.toString(), {
        seconds,
        method: "skip",
        playerState: player.state,
        iframeId: _getIframeId(iframe)
      })
    })
  },

  getCurrentPositionPercentage: (iframe) =>
    _getPlayer(iframe)?.percent || 0.0,

  onPlay: (iframe, callback) =>
    _getPlayer(iframe)?.player?.on("play", callback),

  onEnded: (iframe, callback) =>
    _getPlayer(iframe)?.player?.on("ended", callback),

  // percentage of accumulative playtime / total video time
  // NOTE this can be higher than 100% if someone watches parts more than once
  getPercentageViewed(iframe) {
    if (!this.getAccumulativePlaytime(iframe)) return 0.0
    if (!this.getDuration(iframe)) return 0.0
    return this.getAccumulativePlaytime(iframe) / this.getDuration(iframe)
  },

  setPlaybackRate: (iframe, rate) => (
    _getPlayer(iframe)?.player?.setPlaybackRate(rate)
  ),

  on: (iframe, event, callback) => (
    _getPlayer(iframe)?.player?.on(event, callback)
  ),

  off: (iframe, event, callback) => (
    _getPlayer(iframe)?.player?.off(event, callback)
  ),

  unmute: (iframe) => (
    _getPlayer(iframe)?.player?.setVolume(1)
  )
}

function _createPlayer($iframe, options = {}) {
  const player = {
    player: new Player($iframe[0], {dnt: true}),
    trackingData: $iframe.data("video-tracking"),
    language: $iframe.data("video-language"),
    vimeoId: getVimeoId($iframe)
  }
  player.player.on("timeupdate", (video) => {
    player.duration = video.duration
    player.percent = video.percent
    player.currentTime = video.seconds
    if (options.onUpdateTime) options.onUpdateTime(video)
    saveTimeToStorage(player.vimeoId, video)
  })
  player.player.on("play", () => {
    player.state = "playing"
    _trackFirstVideoPlay(player)
    return videoCaptionsManager.show($iframe)
  })
  player.player.on("pause", () => (player.state = "paused"))
  player.player.on("ended", () => {
    if (!window.gon.currentUser) {
      modal("#video_reg_wall_modal")
    }
    player.state = "ended"
  })
  player.player.on("texttrackchange", (data) =>
    videoCaptionsManager.onChange(data.language)
  )
  const seconds = getTimeFromStorage(player.vimeoId)
  if (seconds) {
    player.player.setCurrentTime(seconds).catch(error => {
      if (error.name === "RangeError") {
        const videos = userSettings.get("videos") || {}
        delete videos[player.vimeoId]
        userSettings.register({videos})
        return
      }

      rollbarHelper.sendErrorMsg(error, {
        seconds,
        method: "setCurrentTime",
        iframeId: _getIframeId($iframe)
      })
    })
  }
  return player
}

function getVimeoId($iframe) {
  const match = $iframe.attr("src").match(/\/video\/(\d+)/)
  return match ? match[1] : undefined
}

function _trackFirstVideoPlay(player) {
  if (player.played) return

  player.played = true
  signals.emit("kenhub.video_played", player.trackingData)
}

// tracks playtime every second
function _trackAccumulativePlaytime(player) {
  return intervals.set(() => _trackVideoPlaytime(player), 1000)
}

// called every 1000ms and adds to the video playtime if still playing
// (this allows to ignore skipping, and we only care about play time)
function _trackVideoPlaytime(player) {
  if (!["playing"].includes(player.state)) return
  if (!player.playtime) player.playtime = 0

  player.playtime += 1
}

function _getPlayer(iframe) {
  const iframeId = _getIframeId(iframe)
  return vimeoManager.players[iframeId]
}

function _getIframeId(iframe) {
  return $(iframe).attr("id")
}

// converts time format (hh:mm:ss/mm:ss/ss) to seconds
// the +time is used to cast the time to a number
function timeToSeconds(time = "00:00") {
  if (!/^(\d|:)+$/.test(time)) return 0

  return time.split(":").reduce((acc, time) => 60 * acc + Number(time)) || 0
}

function getTimeFromStorage(vimeoId) {
  const videos = userSettings.get("videos") || {}
  return videos[vimeoId]
}

function saveTimeToStorage(vimeoId, video) {
  if (!window.gon.canStoreCurrentVideoTime) return

  const videos = userSettings.get("videos") || {}
  // the video should restart a few seconds earlier than when it stopped
  const seconds = video.seconds - 10
  // dont't save the current video time when we reached the end of the video
  // (5% left but max 20 seconds)
  const secondsLeft = video.duration - video.seconds
  if (secondsLeft > Math.min(20, 0.05 * video.duration)) {
    videos[vimeoId] = seconds < 0 ? 0 : seconds
  } else {
    delete videos[vimeoId]
  }
  userSettings.register({videos})
}

// used in GI
// https://app.ghostinspector.com/tests/5887685fbe2d2535b816ab24/steps#18
window.vimeoManager = vimeoManager

export default vimeoManager
